import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { usePasswordResetConfirmMutation } from 'services/kewloxApi';
import { PasswordResetConfirmFormData } from './types';
import { Button } from 'components/ui/button';
import PasswordStrengthIndicator from 'app/components/PasswordStrengthIndicator';
import { Eye, EyeOff, Lock, PartyPopper } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import logo from 'assets/images/logo.png';

export function PasswordResetConfirmPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const uid = searchParams.get('uid');
  const [passwordResetConfirm, { isLoading }] =
    usePasswordResetConfirmMutation();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const schema = Yup.object({
    new_password: Yup.string()
      .required('Champ requis')
      .min(8, 'Le mot de passe doit contenir au moins 8 caractères')
      .matches(/[a-z]/, 'Doit contenir une minuscule')
      .matches(/[A-Z]/, 'Doit contenir une majuscule')
      .matches(/\d/, 'Doit contenir un chiffre'),
    confirmPassword: Yup.string()
      .required('Champ requis')
      .oneOf(
        [Yup.ref('new_password')],
        'Les mots de passe doivent être identiques',
      ),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<PasswordResetConfirmFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      new_password: '',
      confirmPassword: '',
    },
  });

  const newPassword = useWatch({
    control,
    name: 'new_password',
    defaultValue: '',
  });

  const onSubmit = async (values: PasswordResetConfirmFormData) => {
    if (!token || !uid) {
      setErrorMessage('Le lien de réinitialisation est invalide ou a expiré.');
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await passwordResetConfirm({
        token,
        uid,
        new_password: values.new_password,
      }).unwrap();
      setSuccessMessage(
        'Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.',
      );
    } catch (err) {
      setErrorMessage("Une erreur s'est produite lors de la réinitialisation.");
    }
  };

  const renderContent = () => {
    if (!token || !uid) {
      return (
        <div className="text-center">
          <p className="text-sm text-red-600">
            Le lien de réinitialisation est invalide ou a expiré.
          </p>
        </div>
      );
    }

    if (successMessage) {
      return (
        <div className="rounded-md bg-green-50 p-4 text-center">
          <div className="flex flex-col items-center justify-center space-y-4">
            <PartyPopper className="h-12 w-12 text-green-500" />
            <p className="text-sm font-medium text-green-800">
              {successMessage}
            </p>
          </div>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="new_password">Nouveau mot de passe</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400" />
            <Input
              id="new_password"
              type={showPassword ? 'text' : 'password'}
              {...register('new_password')}
              className="pl-10"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff /> : <Eye />}
            </button>
          </div>
          {errors.new_password && (
            <p className="text-sm text-red-600">
              {errors.new_password.message}
            </p>
          )}
        </div>

        <PasswordStrengthIndicator password_={newPassword || ''} />

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400" />
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              {...register('confirmPassword')}
              className="pl-10"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff /> : <Eye />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-sm text-red-600">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>
        {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
        </Button>
      </form>
    );
  };

  return (
    <>
      <Helmet>
        <title>Nouveau mot de passe</title>
        <meta name="description" content="Nouveau mot de passe" />
      </Helmet>
      <div className="flex min-h-screen w-full items-center justify-center bg-gray-100 p-4 dark:bg-gray-950">
        <Card className="w-full max-w-md p-8 shadow-xl transition-shadow duration-300 hover:shadow-2xl">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-6">
              <img src={logo} alt="logo" className="mx-auto h-12 w-auto" />
            </div>
            <CardTitle className="mt-6 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-gray-50">
              Définir un nouveau mot de passe
            </CardTitle>
            {!successMessage && token && uid && (
              <CardDescription className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Veuillez choisir un nouveau mot de passe pour votre compte.
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>{renderContent()}</CardContent>
          <CardFooter className="mt-4 flex justify-center text-sm">
            <Link
              to="/login"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Retour à la connexion
            </Link>
          </CardFooter>
        </Card>
      </div>
    </>
  );
}
